import { Box, Typography, IconButton, Button } from '@mui/material';
import { ArrowLeft, ListPlus, TrashSimple } from '@phosphor-icons/react';
import LayoutWithSidebar from '../../components/common/layoutWithSidebar';
import { HeaderContainer } from '../../components/dashboard/AgentInfoHeader';
import CallDetailTabs from '../../components/Calls/CallDetails/CallDetailtabs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getChatDataApi, removeFromBlacklist } from '../../utils/apis';
import { useParams, useNavigate } from 'react-router';
import { memo, useCallback, useMemo, useState } from 'react';
import { useAlert } from '../../hooks/useAlert';
import BlacklistNumberModal from '../../components/modals/BlacklistNumberModal';
import ConfirmationModal from '../../components/modals/CofirmationModal';

const CallDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showAlert } = useAlert();

  const [blacklistModalOpen, setBlacklistModalOpen] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

  const handleBack = useCallback(() => {
    navigate('/calls');
  }, [navigate]);

  const { data, isLoading } = useQuery({
    queryKey: ['chatData', id],
    queryFn: () => getChatDataApi({ id: id || '' }),
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 5,
    retry: 0,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const chatData = useMemo(() => data?.data?.chat, [data]);

  const { mutate: removeMutation, isPending: isRemovePending } = useMutation({
    mutationFn: removeFromBlacklist,
    onSuccess: () => {
      showAlert('Number removed from blacklist successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['agentCallsData'] });
      queryClient.invalidateQueries({ queryKey: ['chatData', id] });
      setRemoveConfirmOpen(false);
    },
    onError: (err: any) => {
      showAlert(
        err?.response?.data?.message ||
          'Failed to remove number from blacklist',
        'error'
      );
    },
  });

  const handleRemoveConfirm = useCallback(() => {
    if (chatData?.blacklistedNumber) {
      removeMutation({ phoneNumber: chatData.blacklistedNumber });
    }
  }, [chatData?.blacklistedNumber, removeMutation]);

  const hasFromTo = Boolean(chatData?.from) || Boolean(chatData?.to);
  const isBlacklisted = Boolean(chatData?.blacklisted);

  return (
    <LayoutWithSidebar margin={2}>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <HeaderContainer
          sx={{
            bgcolor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: '12px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <IconButton onClick={handleBack} size="small" sx={{ mr: 0.5 }}>
              <ArrowLeft size={20} color="#000000" />
            </IconButton>
            <Typography
              variant="h6"
              color="custom.textBlue"
              sx={{
                fontSize: '24px',
                fontWeight: 600,
                lineHeight: 'normal',
                letterSpacing: '-0.16px',
                textTransform: 'capitalize',
              }}
            >
              {chatData?.agentName} / {chatData?.callType}
            </Typography>
          </Box>
          {hasFromTo && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {isBlacklisted ? (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<TrashSimple size={18} />}
                  onClick={() => setRemoveConfirmOpen(true)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    boxShadow: 'none',
                  }}
                >
                  Remove from blacklist
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<ListPlus size={18} />}
                  onClick={() => setBlacklistModalOpen(true)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    boxShadow: 'none',
                  }}
                >
                  Add to blacklist
                </Button>
              )}
            </Box>
          )}
        </HeaderContainer>

        <CallDetailTabs
          messages={chatData?.chat}
          id={chatData?._id}
          agentName={chatData?.agentName}
          callType={chatData?.callType}
          to={chatData?.to}
          from={chatData?.from}
          config={chatData?.config}
          isLoading={isLoading}
          audio={chatData?.audio}
          cost={chatData?.cost}
          billedFor={chatData?.time}
          sid={chatData?.uid}
          selectedAssistantId={chatData?.agentId}
        />

        {chatData && (
          <BlacklistNumberModal
            open={blacklistModalOpen}
            onClose={() => setBlacklistModalOpen(false)}
            row={{ from: chatData.from ?? '', to: chatData.to ?? '' }}
            showAlert={showAlert}
            onSuccess={() =>
              queryClient.invalidateQueries({ queryKey: ['chatData', id] })
            }
          />
        )}

        <ConfirmationModal
          open={removeConfirmOpen}
          onClose={() => setRemoveConfirmOpen(false)}
          onConfirm={handleRemoveConfirm}
          isPending={isRemovePending}
          title="Remove from blacklist"
          description="Are you sure you want to remove this number from the blacklist?"
          confirmButtonText="Remove"
          cancelButtonText="Cancel"
          confirmButtonColor="error"
        />
      </Box>
    </LayoutWithSidebar>
  );
};

export default memo(CallDetails);
